import React, { useEffect, useRef } from 'react';

import { createRoot } from 'react-dom/client';
import { IntlProvider } from 'react-intl';

import { getDocument } from '@atlaskit/browser-apis';
import { RovoIcon } from '@atlaskit/logo';
import { GeneratedAvatar } from '@atlaskit/rovo-agent-components/ui/agent-avatar/GeneratedAvatar';
import { logException } from '@atlassian/conversation-assistant-instrumentation/services/sentry';
import { type Agent } from '@atlassian/conversation-assistant-service-api/types/assistance-service';

import { useAgentFaviconCache } from './utils/agent-favicon-cache';

// Observer component for RovoIcon rendering
const RovoIconObserver = ({ onLoad }: { onLoad: () => void }) => {
	useEffect(() => {
		onLoad();
	}, [onLoad]);
	return null;
};

// Create favicon by rendering RovoIcon off-screen for the default agent
const createRovoIconFavicon = async (): Promise<string> => {
	return new Promise((resolve, reject) => {
		const document = getDocument();
		if (!document) {
			reject(new Error('Document not available'));
			return;
		}
		const container = document.createElement('div');
		container.style.position = 'absolute';
		container.style.left = '-9999px';
		container.style.top = '-9999px';
		container.style.width = '32px';
		container.style.height = '32px';
		document.body.appendChild(container);

		const cleanup = () => {
			try {
				if (root) {
					root.unmount();
				}
				if (document.body.contains(container)) {
					document.body.removeChild(container);
				}
			} catch (e) {
				// Expected cleanup errors - silently continue
			}
		};

		const handleRenderComplete = () => {
			try {
				const svgElement = container.querySelector('svg');
				if (svgElement) {
					const svgData = new XMLSerializer().serializeToString(svgElement);
					const svgBlob = new Blob([svgData], { type: 'image/svg+xml;charset=utf-8' });
					const svgUrl = URL.createObjectURL(svgBlob);

					const img = new Image();
					const canvas = document.createElement('canvas');
					canvas.width = 32;
					canvas.height = 32;
					const ctx = canvas.getContext('2d');

					img.onload = () => {
						if (ctx) {
							ctx.drawImage(img, 0, 0, 32, 32);
							URL.revokeObjectURL(svgUrl);
							cleanup();
							resolve(canvas.toDataURL('image/png'));
						}
					};
					img.src = svgUrl;
				} else {
					cleanup();
					const exception = new Error('No SVG found for RovoIcon');
					logException({
						exception,
						name: 'NoSVGFoundForRovoIcon',
						tags: {
							component: 'AgentFavicon',
						},
					});
					reject(exception);
				}
			} catch (error) {
				cleanup();
				const exception = new Error('Failed to create RovoIcon favicon');
				logException({
					exception,
					name: 'FailedToCreateRovoIconFavicon',
					tags: {
						component: 'AgentFavicon',
					},
				});
				reject(error);
			}
		};

		const root = createRoot(container);
		root.render(
			<IntlProvider locale="en">
				<RovoIconObserver onLoad={handleRenderComplete} />
				<RovoIcon size="medium" />
			</IntlProvider>,
		);
	});
};

// Create favicon by rendering GeneratedAvatar off-screen and extracting its visual output
const createAgentFaviconFromGeneratedAvatar = async (
	agent: Agent,
	cache: ReturnType<typeof useAgentFaviconCache>,
	pageTitle?: string,
): Promise<string | null> => {
	// Check cache first
	const agentName = agent.name;
	const cachedFavicon = cache.get(agentName);
	if (cachedFavicon) {
		return cachedFavicon;
	}

	// Create a promise that resolves when the avatar is rendered
	return new Promise((resolve, reject) => {
		// If agent has an icon URL, use it directly
		if (agent.icon) {
			const faviconUrl = agent.icon;
			cache.set(agentName, faviconUrl);
			resolve(faviconUrl);
			return;
		}

		// Special handling for default Rovo agent
		if ((agent.is_default && agent.creator_type === 'SYSTEM') || !agent) {
			createRovoIconFavicon()
				.then((faviconUrl) => {
					cache.set(agentName, faviconUrl);
					resolve(faviconUrl);
				})
				.catch(reject);
			return;
		}

		// Create off-screen container
		const document = getDocument();
		if (!document) {
			throw new Error('Document not available');
		}

		const container = document.createElement('div');
		container.style.position = 'absolute';
		container.style.left = '-9999px';
		container.style.top = '-9999px';
		container.style.width = '32px';
		container.style.height = '32px';
		container.style.overflow = 'hidden';
		document.body.appendChild(container);

		const timeout = setTimeout(() => {
			cleanup();
			reject(new Error('Timeout'));
		}, 5000);

		const cleanup = () => {
			try {
				if (root) {
					root.unmount();
				}
				if (document.body.contains(container)) {
					document.body.removeChild(container);
				}
			} catch (e) {
				// Expected cleanup errors - silently continue
			}
		};

		// Render GeneratedAvatar with proper context
		const root = createRoot(container);

		// Render the component and wait for it to complete
		root.render(
			<IntlProvider locale="en">
				<GeneratedAvatar
					agentNamedId={agent.external_config_reference || agent.named_id}
					agentId={agent.id}
					agentIdentityAccountId={agent.identity_account_id}
					size="medium"
					onLoad={async () => {
						try {
							const faviconUrl = await extractFaviconFromContainer(container);
							clearTimeout(timeout);
							cleanup();

							// Cache the generated favicon
							if (faviconUrl) {
								cache.set(agentName, faviconUrl);
							}

							resolve(faviconUrl);
						} catch (error) {
							clearTimeout(timeout);
							cleanup();
							reject(error);
						}
					}}
				/>
			</IntlProvider>,
		);
	});
};

// Extract favicon from the rendered AgentAvatar container
const extractFaviconFromContainer = async (container: HTMLElement): Promise<string> => {
	// Look for an img element first (for agents with icon URLs)
	const imgElement = container.querySelector('img');
	if (imgElement && imgElement.src && imgElement.complete) {
		// If it's an external URL (not data:), we need to convert it to avoid CORS issues
		if (!imgElement.src.startsWith('data:')) {
			try {
				const document = getDocument();
				if (!document) {
					return imgElement.src; // Fall back to original URL
				}
				const canvas = document.createElement('canvas');
				canvas.width = 32;
				canvas.height = 32;
				const ctx = canvas.getContext('2d');

				if (ctx) {
					// Draw the image to canvas
					ctx.drawImage(imgElement, 0, 0, 32, 32);
					return canvas.toDataURL('image/png');
				}
			} catch (e) {
				// Could not convert img to canvas, using src directly
			}
		}

		return imgElement.src;
	}

	// Look for SVG element (for OOTB generated avatars)
	const svgElement = container.querySelector('svg');
	if (svgElement) {
		// Convert SVG to canvas
		const document = getDocument();
		if (!document) {
			throw new Error('Document not available');
		}
		const canvas = document.createElement('canvas');
		canvas.width = 32;
		canvas.height = 32;
		const ctx = canvas.getContext('2d');

		if (!ctx) {
			throw new Error('Could not get canvas context');
		}

		// Create a data URL from the SVG
		const svgData = new XMLSerializer().serializeToString(svgElement);
		const svgBlob = new Blob([svgData], { type: 'image/svg+xml;charset=utf-8' });
		const svgUrl = URL.createObjectURL(svgBlob);

		// Create an image from the SVG and draw it to canvas
		const img = new Image();

		return new Promise<string>((resolve, reject) => {
			let urlRevoked = false;

			const cleanup = () => {
				if (!urlRevoked) {
					URL.revokeObjectURL(svgUrl);
					urlRevoked = true;
				}
			};

			img.onload = () => {
				try {
					// Scale the SVG to fit 32x32
					ctx.drawImage(img, 0, 0, 32, 32);
					cleanup();
					resolve(canvas.toDataURL('image/png'));
				} catch (error) {
					cleanup();
					reject(error);
				}
			};

			img.onerror = () => {
				cleanup();
				reject(new Error('Failed to load SVG as image'));
			};

			img.src = svgUrl;
		});
	}

	// For generated avatars without SVG, extract the visual characteristics and recreate
	const avatarElement = container.querySelector(
		'[data-testid*="avatar"], [class*="avatar"], div > div',
	);
	if (avatarElement) {
		const computedStyle = window.getComputedStyle(avatarElement);
		const bgColor = computedStyle.backgroundColor || '#0052CC';
		const textContent = avatarElement.textContent?.trim()?.charAt(0)?.toUpperCase() || 'A';

		// Create canvas and draw the favicon
		const document = getDocument();
		if (!document) {
			throw new Error('Document not available');
		}
		const canvas = document.createElement('canvas');
		canvas.width = 32;
		canvas.height = 32;
		const ctx = canvas.getContext('2d');

		if (!ctx) {
			throw new Error('Could not get canvas context');
		}

		// Draw background circle
		ctx.beginPath();
		ctx.arc(16, 16, 15, 0, 2 * Math.PI);
		ctx.fillStyle = bgColor;
		ctx.fill();

		// Draw text
		ctx.fillStyle = '#FFFFFF';
		ctx.font = 'bold 16px Arial';
		ctx.textAlign = 'center';
		ctx.textBaseline = 'middle';
		ctx.fillText(textContent, 16, 16);

		return canvas.toDataURL('image/png');
	}

	throw new Error('Could not extract favicon from container - no suitable elements found');
};

const createRovoIconAndSetAsFavicon = async (cache: ReturnType<typeof useAgentFaviconCache>) => {
	try {
		const defaultAgentName = 'default-rovo-agent';

		// Check cache first
		const cachedFavicon = cache.get(defaultAgentName);
		if (cachedFavicon) {
			updateFavicon(cachedFavicon);
			return;
		}

		const rovoFaviconUrl = await createRovoIconFavicon();
		cache.set(defaultAgentName, rovoFaviconUrl);
		updateFavicon(rovoFaviconUrl);
	} catch (error) {
		setDefaultAtlassianFavicon();
	}
};

// Set favicon to default Atlassian logo
const setDefaultAtlassianFavicon = () => {
	try {
		const atlassianLogoUrl = '/assets/logos/atlassian-home-apple-touch-icon.png';
		updateFavicon(atlassianLogoUrl);
	} catch (error) {
		// Fallback to browser default favicon by clearing all agent favicons
		updateFavicon('');
		logException({
			exception: error,
			name: 'FailedToSetDefaultAtlassianFavicon',
			tags: {
				component: 'AgentFavicon',
			},
		});
	}
};

const updateFavicon = (faviconUrl: string) => {
	const document = getDocument();
	if (!document) {
		return; // Skip favicon updates in SSR or when document is not available
	}

	if (!faviconUrl) {
		// Remove any existing agent-specific favicons
		const existingLinks = document.querySelectorAll("link[rel*='icon'][data-agent-favicon]");
		existingLinks.forEach((link) => link.remove());
		return;
	}

	try {
		// Remove any existing favicon links
		const existingLinks = document.querySelectorAll("link[rel*='icon']");
		existingLinks.forEach((link) => link.remove());

		// Create new favicon link
		const link = document.createElement('link');
		link.type = 'image/png';
		link.rel = 'icon';
		link.href = faviconUrl;
		link.setAttribute('data-agent-favicon', 'true');

		document.getElementsByTagName('head')[0].appendChild(link);

		// Also create shortcut icon for better browser support
		const shortcutLink = document.createElement('link');
		shortcutLink.type = 'image/png';
		shortcutLink.rel = 'shortcut icon';
		shortcutLink.href = faviconUrl;
		shortcutLink.setAttribute('data-agent-favicon', 'true');
		document.getElementsByTagName('head')[0].appendChild(shortcutLink);
	} catch (error) {
		logException({
			exception: error,
			name: 'FailedToUpdateFavicon',
			tags: {
				component: 'AgentFavicon',
			},
		});
		return;
	}
};

type AgentFaviconProps = {
	selectedAgent?: Agent | null;
	currentConversationId?: string;
};

/**
 * Component that dynamically updates the page favicon based on the selected agent
 * Reuses the actual AgentAvatar component to ensure perfect visual consistency
 */
export const AgentFavicon = ({ selectedAgent, currentConversationId }: AgentFaviconProps) => {
	const prevAgentRef = useRef<Agent | null>(null);
	const cache = useAgentFaviconCache();

	// Generate favicon URL by rendering AgentAvatar
	useEffect(() => {
		if (!selectedAgent) {
			createRovoIconAndSetAsFavicon(cache);
			return;
		}

		// Only regenerate if the agent actually changed
		const agentChanged = prevAgentRef.current?.id !== selectedAgent.id;

		if (!agentChanged) {
			return;
		}

		prevAgentRef.current = selectedAgent;
		createAgentFaviconFromGeneratedAvatar(selectedAgent, cache)
			.then((generatedFaviconUrl) => {
				if (generatedFaviconUrl) {
					updateFavicon(generatedFaviconUrl);
				} else {
					// default Rovo icon
					createRovoIconAndSetAsFavicon(cache);
				}
			})
			.catch((error) => {
				// within this catch try to set to rovo and if fails then set to default Atlassian
				createRovoIconAndSetAsFavicon(cache);
			});
	}, [selectedAgent, currentConversationId, cache]);

	return undefined;
};
